type UnitSpec = {
  category: string
  toBase: (value: number) => number
  fromBase: (value: number) => number
}

const scalar = (factor: number, category: string): UnitSpec => ({
  category,
  toBase: (v) => v * factor,
  fromBase: (v) => v / factor,
})

const UNIT_SPECS = {
  milliseconds: scalar(0.001, 'time'),
  seconds: scalar(1, 'time'),
  minutes: scalar(60, 'time'),
  hours: scalar(3600, 'time'),
  days: scalar(86400, 'time'),
  weeks: scalar(604800, 'time'),

  millimeters: scalar(0.001, 'length'),
  centimeters: scalar(0.01, 'length'),
  meters: scalar(1, 'length'),
  kilometers: scalar(1000, 'length'),
  inches: scalar(0.0254, 'length'),
  feet: scalar(0.3048, 'length'),
  yards: scalar(0.9144, 'length'),
  miles: scalar(1609.344, 'length'),

  milligrams: scalar(0.000001, 'mass'),
  grams: scalar(0.001, 'mass'),
  kilograms: scalar(1, 'mass'),
  metricTons: scalar(1000, 'mass'),
  ounces: scalar(0.0283495, 'mass'),
  pounds: scalar(0.453592, 'mass'),
  tons: scalar(907.185, 'mass'),

  kelvin: { category: 'temperature', toBase: (v) => v, fromBase: (v) => v },
  celsius: { category: 'temperature', toBase: (v) => v + 273.15, fromBase: (v) => v - 273.15 },
  fahrenheit: {
    category: 'temperature',
    toBase: (v) => ((v - 32) * 5) / 9 + 273.15,
    fromBase: (v) => ((v - 273.15) * 9) / 5 + 32,
  },
} satisfies Record<string, UnitSpec>

/** Alternative unit names */
const UNIT_ALIASES: Record<string, keyof typeof UNIT_SPECS | undefined> = {
  millisecond: 'milliseconds',
  second: 'seconds',
  minute: 'minutes',
  hour: 'hours',
  day: 'days',
  week: 'weeks',

  ms: 'milliseconds',
  s: 'seconds',
  min: 'minutes',
  hr: 'hours',

  millimeter: 'millimeters',
  centimeter: 'centimeters',
  meter: 'meters',
  kilometer: 'kilometers',
  inch: 'inches',
  foot: 'feet',
  yard: 'yards',
  mile: 'miles',

  mm: 'millimeters',
  cm: 'centimeters',
  km: 'kilometers',
  in: 'inches',
  ft: 'feet',
  yd: 'yards',
  mi: 'miles',

  milligram: 'milligrams',
  gram: 'grams',
  kilogram: 'kilograms',
  ounce: 'ounces',
  pound: 'pounds',
  ton: 'tons',

  mg: 'milligrams',
  g: 'grams',
  kg: 'kilograms',
  oz: 'ounces',
  lb: 'pounds',
  lbs: 'pounds',

  C: 'celsius',
  F: 'fahrenheit',
  K: 'kelvin',
  c: 'celsius',
  f: 'fahrenheit',
  centigrade: 'celsius',
}

export function convert(value: number, fromUnit: string, toUnit: string): number {
  const specs = UNIT_SPECS as Record<string, UnitSpec>
  const fromSpec = specs[UNIT_ALIASES[fromUnit] ?? fromUnit]
  const toSpec = specs[UNIT_ALIASES[toUnit] ?? toUnit]

  if (!fromSpec || !toSpec) {
    console.warn(`Unknown unit: ${!fromSpec ? fromUnit : toUnit}`)
    return value
  }

  if (fromSpec === toSpec) {
    return value
  }

  if (fromSpec.category !== toSpec.category) {
    console.warn(
      `Cannot convert from ${fromUnit} (${fromSpec.category}) to ${toUnit} (${toSpec.category})`
    )
    return value
  }

  const baseValue = fromSpec.toBase(value)
  return toSpec.fromBase(baseValue)
}
